import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges
} from '@angular/core';

import {
    PlanningAssignment,
    ResourceEmployee,
    ResourceWeekendDay,
    ResourceCategory,
    ResourceCustomer,
    ResourcePlant
} from '../../../Models/resource.models';

import {
    ResourceCapacityService
} from '../../../Services/resource-capacity.service';

@Component({
    selector: 'app-resource-assignment-editor',
    templateUrl: './resource-assignment-editor.component.html',
    styleUrls: ['./resource-assignment-editor.component.css']
})
export class ResourceAssignmentEditorComponent implements OnChanges {

    @Input() assignment: PlanningAssignment | null = null;
    @Input() employees: ResourceEmployee[] = [];
    @Input() allAssignments: PlanningAssignment[] = [];

    /** Kategorien aus ResourceCategoryDefs (vom Planungs-Container geladen). */
    @Input() categories: ResourceCategory[] = [];

    /** Während eines laufenden Speichervorgangs werden die Buttons gesperrt. */
    @Input() saving = false;

    @Output() saved = new EventEmitter<PlanningAssignment>();
    @Output() deleted = new EventEmitter<number>();
    @Output() closed = new EventEmitter<void>();

    @Input() customers: ResourceCustomer[] = [];
    @Input() plants: ResourcePlant[] = [];

    /** false = Option „Outlook-Termin senden“ ausblenden (z. B. „Mein Kalender“: Outlook wird nie verändert). */
    @Input() allowOutlookInvitation = true;

    form!: PlanningAssignment;
    selectedEmployeeIds: string[] = [];
    weekendOptions: ResourceWeekendDay[] = [];

    errorMessage = '';
    overloadWarnings: string[] = [];
    showOverloadConfirmation = false;

    constructor(
        private capacityService: ResourceCapacityService
    ) { }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['assignment'] && this.assignment) {
            this.initializeForm();
        }
    }

    get isEditing(): boolean {
        return this.form?.id > 0;
    }

    get selectedEmployees(): ResourceEmployee[] {
        return this.employees.filter(employee =>
            this.selectedEmployeeIds.includes(employee.id)
        );
    }

    get weekendDates(): string[] {
        if (!this.form?.startDate || !this.form?.endDate) {
            return [];
        }

        const result: string[] = [];
        const current = this.parseDate(this.form.startDate);
        const end = this.parseDate(this.form.endDate);

        if (current > end) {
            return [];
        }

        while (current <= end) {
            if (current.getDay() === 0 || current.getDay() === 6) {
                result.push(this.dateKey(current));
            }

            current.setDate(current.getDate() + 1);
        }

        return result;
    }

    get availablePlants(): ResourcePlant[] {
        if (!this.form?.customerId) {
            return [];
        }

        return this.plants.filter(
            plant => plant.customerId === this.form.customerId
        );
    }

    onCustomerChanged(customerId: number | null): void {
        this.form.customerId = customerId;
        this.form.plantId = null;
    }
    resetTimeWarnings(): void {
        this.overloadWarnings = [];
        this.showOverloadConfirmation = false;
    }

    initializeForm(): void {
        if (!this.assignment) {
            return;
        }

        this.form = {
            ...this.assignment,
            participants: this.assignment.participants.map(p => ({ ...p })),
            weekendDates: [...this.assignment.weekendDates],
            weekendDays: (this.assignment.weekendDays ?? []).map(day => ({
                date: day.date,
                hoursByEmployee: { ...day.hoursByEmployee }
            }))
        };

        // Kategorie-Anzeige an die geladenen Kategorien angleichen (z. B. wenn
        // die Kategorie des Termins inzwischen gelöscht wurde).
        const category = this.categories.find(c => c.id === this.form.categoryId);
        if (category) {
            this.form.category = category.name;
            if (!this.form.color) {
                this.form.color = category.color;
            }
        } else {
            this.form.categoryId = null;
        }

        this.selectedEmployeeIds =
            this.form.participants.map(p => p.employeeId);

        this.errorMessage = '';
        this.overloadWarnings = [];
        this.showOverloadConfirmation = false;

        this.weekendOptions = [];
        this.rebuildWeekendOptions();
    }

    onEmployeesChanged(ids: string[]): void {
        this.selectedEmployeeIds = ids;

        this.form.participants = ids.map(employeeId => {
            const existing = this.form.participants.find(
                p => p.employeeId === employeeId
            );

            return existing
                ? { ...existing }
                : { employeeId, utilizationPercent: 100 };
        });

        for (const day of this.weekendOptions) {
            for (const key of Object.keys(day.hoursByEmployee)) {
                if (!ids.includes(key)) {
                    delete day.hoursByEmployee[key];
                }
            }
        }

        this.resetWarnings();
    }

    onCategoryChanged(categoryId: number | null): void {
        const category = this.categories.find(
            c => c.id === categoryId
        );

        if (category) {
            this.form.categoryId = category.id;
            this.form.category = category.name;
            this.form.color = category.color;
        } else {
            this.form.categoryId = null;
        }

        this.resetWarnings();
    }

    onDatesChanged(): void {
        this.rebuildWeekendOptions();
        this.resetWarnings();
    }

    getPercent(employeeId: string): number {
        return this.form.participants.find(
            p => p.employeeId === employeeId
        )?.utilizationPercent ?? 100;
    }

    setPercent(employeeId: string, percent: number): void {
        const participant = this.form.participants.find(
            p => p.employeeId === employeeId
        );

        if (participant) {
            participant.utilizationPercent = Number(percent);
        }

        this.resetWarnings();
    }

    isWeekendSelected(date: string): boolean {
        return this.form.weekendDates.includes(date);
    }

    toggleWeekend(date: string, checked: boolean): void {
        if (checked) {
            if (!this.form.weekendDates.includes(date)) {
                this.form.weekendDates.push(date);
            }
        } else {
            this.form.weekendDates =
                this.form.weekendDates.filter(d => d !== date);
        }

        this.resetWarnings();
    }

    getWeekendHours(date: string, employeeId: string): number {
        const day = this.weekendOptions.find(d => d.date === date);
        return day?.hoursByEmployee[employeeId] ?? 0;
    }

    setWeekendHours(
        date: string,
        employeeId: string,
        hours: number
    ): void {
        const day = this.weekendOptions.find(d => d.date === date);

        if (day) {
            day.hoursByEmployee[employeeId] = Number(hours);
        }

        this.resetWarnings();
    }

    save(): void {
        this.errorMessage = '';

        if (this.saving) {
            return;
        }

        if (!this.form.title.trim()) {
            this.errorMessage = 'Bitte eine Terminbezeichnung eingeben.';
            return;
        }

        if (!this.form.categoryId) {
            this.errorMessage = 'Bitte eine Kategorie auswählen.';
            return;
        }

        if (!this.form.startDate || !this.form.endDate) {
            this.errorMessage = 'Bitte Beginn und Ende auswählen.';
            return;
        }

        if (this.form.startDate > this.form.endDate) {
            this.errorMessage = 'Das Enddatum liegt vor dem Beginn.';
            return;
        }
        if (!this.form.startTime || !this.form.endTime) {
            this.errorMessage = 'Bitte Beginn- und Enduhrzeit angeben.';
            return;
        }

        const startDateTime =
            `${this.form.startDate}T${this.form.startTime}`;

        const endDateTime =
            `${this.form.endDate}T${this.form.endTime}`;

        if (startDateTime >= endDateTime) {
            this.errorMessage =
                'Das Ende muss zeitlich nach dem Beginn liegen.';
            return;
        }

        if (this.selectedEmployeeIds.length === 0) {
            this.errorMessage = 'Bitte mindestens einen Mitarbeiter auswählen.';
            return;
        }

        if (this.form.participants.some(p =>
            !Number.isFinite(p.utilizationPercent) ||
            p.utilizationPercent < 0 ||
            p.utilizationPercent > 100
        )) {
            this.errorMessage =
                'Die Auslastung muss zwischen 0 und 100 % liegen.';
            return;
        }

        for (const date of this.form.weekendDates) {
            const day = this.weekendOptions.find(d => d.date === date);

            if (!day) {
                this.errorMessage = 'Ungültige Wochenendplanung.';
                return;
            }

            for (const employeeId of this.selectedEmployeeIds) {
                const hours = day.hoursByEmployee[employeeId] ?? 0;

                if (
                    !Number.isFinite(hours) ||
                    hours < 0 ||
                    hours > 24
                ) {
                    this.errorMessage =
                        'Wochenendstunden müssen zwischen 0 und 24 liegen.';
                    return;
                }
            }
        }

        this.overloadWarnings = this.calculateWarnings();

        if (
            this.overloadWarnings.length > 0 &&
            !this.showOverloadConfirmation
        ) {
            this.showOverloadConfirmation = true;
            return;
        }

        const result: PlanningAssignment = {
            ...this.form,
            title: this.form.title.trim(),
            participants: this.form.participants.map(p => ({ ...p })),
            weekendDates: [...this.form.weekendDates],
            weekendDays: this.weekendOptions
                .filter(day => this.form.weekendDates.includes(day.date))
                .map(day => ({
                    date: day.date,
                    hoursByEmployee: { ...day.hoursByEmployee }
                })),
            sourceExternalUid: this.assignment?.sourceExternalUid ?? null,
            sourceExternalUserId: this.assignment?.sourceExternalUserId ?? null,
            sourceExternalStart: this.assignment?.sourceExternalStart ?? null,
        };

        this.saved.emit(result);
    }

    delete(): void {
        if (!this.isEditing || this.saving) {
            return;
        }

        if (window.confirm('Diesen Termin wirklich löschen?')) {
            this.deleted.emit(this.form.id);
        }
    }

    formatDate(date: string): string {
        return this.parseDate(date).toLocaleDateString('de-AT', {
            weekday: 'long',
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    }

    private rebuildWeekendOptions(): void {
        const previous = this.weekendOptions.length
            ? this.weekendOptions
            : (this.form.weekendDays ?? []);

        this.weekendOptions = this.weekendDates.map(date => {
            const existing = previous.find(day => day.date === date);

            return {
                date,
                hoursByEmployee: existing
                    ? { ...existing.hoursByEmployee }
                    : {}
            };
        });

        this.form.weekendDates = this.form.weekendDates.filter(
            date => this.weekendDates.includes(date)
        );
    }

    private calculateWarnings(): string[] {

        const candidate: PlanningAssignment = {
            ...this.form,
            participants: this.form.participants.map(p => ({ ...p })),
            weekendDates: [...this.form.weekendDates],
            weekendDays: this.weekendOptions
                .filter(day => this.form.weekendDates.includes(day.date))
                .map(day => ({
                    date: day.date,
                    hoursByEmployee: { ...day.hoursByEmployee }
                }))
        };

        const assignments = [
            ...this.allAssignments.filter(a => a.id !== candidate.id),
            candidate
        ];

        return this.capacityService.getOverloadWarnings(
            this.selectedEmployees,
            assignments,
            candidate.startDate,
            candidate.endDate
        );
    }

    private resetWarnings(): void {
        this.overloadWarnings = [];
        this.showOverloadConfirmation = false;
    }

    private parseDate(value: string): Date {
        const [year, month, day] = value.split('-').map(Number);
        return new Date(year, month - 1, day);
    }

    private dateKey(date: Date): string {
        return [
            date.getFullYear(),
            String(date.getMonth() + 1).padStart(2, '0'),
            String(date.getDate()).padStart(2, '0')
        ].join('-');
    }
}
